import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { popularEmojiSections, popularEmojiSearch } from '@shared/constants/popularEmojis';
import {
  CATEGORY_FALLBACK_ICON,
  CATEGORY_TAB_META,
  EMPTY_PICKER_STYLE,
  TOP_TAB_COUNT,
  TONE_BUTTONS,
  getAvailableEmojis,
  getCurrentSectionId,
  getNormalizedSections,
  getOrderedTabSections,
  getPickerPosition,
  getScrollTargetTop,
  getSearchScore,
  getTabSections,
  getUniqueItems,
} from './helpers';
import {
  Wrapper,
  EmojiButton,
  EmojiPicker,
  EmojiSearchInput,
  EmojiControlsRow,
  EmojiTabs,
  EmojiTab,
  EmojiTabIcon,
  EmojiOverflowMenu,
  EmojiOverflowMenuItem,
  ToneSelector,
  ToneButton,
  EmojiPickerScroll,
  EmojiSectionTitle,
  EmojiGrid,
  EmojiItem,
} from './styles';

export const EmojiPickerButton = ({
  emojis,
  sections,
  searchIndex,
  onSelect,
  ariaLabel,
  buttonEmoji,
  recentStorageKey,
  maxRecent,
}) => {
  const wrapperRef = useRef(null);
  const pickerRef = useRef(null);
  const searchInputRef = useRef(null);
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTone, setSelectedTone] = useState(0);
  const [activeTab, setActiveTab] = useState('');
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const [recentEmojis, setRecentEmojis] = useState([]);
  const [pickerStyle, setPickerStyle] = useState(EMPTY_PICKER_STYLE);
  const overflowMenuRef = useRef(null);
  const overflowButtonRef = useRef(null);
  const uniqueEmojis = useMemo(() => getUniqueItems(emojis), [emojis]);
  const normalizedSections = useMemo(
    () => getNormalizedSections(sections, uniqueEmojis),
    [sections, uniqueEmojis]
  );
  const availableEmojis = useMemo(() => getAvailableEmojis(normalizedSections), [normalizedSections]);

  const closePicker = useCallback(() => {
    setIsOpen(false);
    setIsOverflowOpen(false);
    setSearchTerm('');
  }, []);

  const onToggleOpen = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const resolveEmojiByTone = useCallback(
    (baseEmoji) => {
      if (!selectedTone) {
        return baseEmoji;
      }

      const skins = searchIndex[baseEmoji]?.skins;
      if (!skins) {
        return baseEmoji;
      }

      return skins[selectedTone] || baseEmoji;
    },
    [searchIndex, selectedTone]
  );

  const onEmojiClick = useCallback(
    (emoji) => {
      setRecentEmojis((prevRecent) => {
        const nextRecent = [emoji, ...prevRecent.filter((item) => item !== emoji)].slice(0, maxRecent);

        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            window.localStorage.setItem(recentStorageKey, JSON.stringify(nextRecent));
          } catch (error) {
            // localStorage can fail in private mode or quota limits.
          }
        }

        return nextRecent;
      });

      onSelect(resolveEmojiByTone(emoji));
      closePicker();
    },
    [closePicker, maxRecent, onSelect, recentStorageKey, resolveEmojiByTone]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      const raw = window.localStorage.getItem(recentStorageKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const emojiSet = new Set(availableEmojis);
        const nextRecent = parsed
          .filter((emoji) => typeof emoji === 'string' && emojiSet.has(emoji))
          .slice(0, maxRecent);
        setRecentEmojis(nextRecent);
      }
    } catch (error) {
      // Invalid localStorage data should not block rendering.
    }
  }, [availableEmojis, maxRecent, recentStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      const rawTone = window.localStorage.getItem(`${recentStorageKey}_tone`);
      const tone = Number(rawTone);
      if (Number.isFinite(tone) && tone >= 0 && tone <= 5) {
        setSelectedTone(tone);
      }
    } catch (error) {
      // localStorage can fail in private mode or quota limits.
    }
  }, [recentStorageKey]);

  const emojiSections = useMemo(
    () => normalizedSections.map((section) => ({
      ...section,
      emojis: section.emojis.filter((emoji) => !recentEmojis.includes(emoji)),
    })).filter((section) => section.emojis.length > 0),
    [normalizedSections, recentEmojis]
  );

  const tabSections = useMemo(() => getTabSections(emojiSections, recentEmojis), [emojiSections, recentEmojis]);
  const orderedTabSections = useMemo(() => getOrderedTabSections(tabSections), [tabSections]);
  const fixedTabs = orderedTabSections.slice(0, TOP_TAB_COUNT);
  const overflowTabs = orderedTabSections.slice(TOP_TAB_COUNT);
  const showMoreTabs = overflowTabs.length > 0;

  useEffect(() => {
    if (tabSections.length === 0) {
      return;
    }

    const activeTabExists = tabSections.some((section) => section.id === activeTab);
    if (!activeTabExists) {
      setActiveTab(tabSections[0].id);
    }
  }, [activeTab, tabSections]);

  const normalizedSearch = useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);

  const searchResults = useMemo(() => {
    if (!normalizedSearch) {
      return [];
    }

    return availableEmojis
      .map((emoji) => getSearchScore({ emoji, normalizedSearch, searchIndex, recentEmojis }))
      .filter((item) => item.score > 0 && !recentEmojis.includes(item.emoji))
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return a.order - b.order;
      })
      .slice(0, 140)
      .map((item) => item.emoji);
  }, [availableEmojis, normalizedSearch, recentEmojis, searchIndex]);

  const updatePickerPosition = useCallback(() => {
    if (!wrapperRef.current || typeof window === 'undefined') {
      return;
    }

    setPickerStyle(getPickerPosition({
      wrapperRect: wrapperRef.current.getBoundingClientRect(),
      pickerHeight: pickerRef.current?.offsetHeight || 352,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    }));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updatePickerPosition();

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    const onDocumentMouseDown = (event) => {
      const clickedInsideToggle = wrapperRef.current && wrapperRef.current.contains(event.target);
      const clickedInsidePicker = pickerRef.current && pickerRef.current.contains(event.target);
      const clickedOverflowMenu = overflowMenuRef.current && overflowMenuRef.current.contains(event.target);
      const clickedOverflowButton = overflowButtonRef.current && overflowButtonRef.current.contains(event.target);

      if (!clickedOverflowMenu && !clickedOverflowButton) {
        setIsOverflowOpen(false);
      }

      if (!clickedInsideToggle && !clickedInsidePicker) {
        closePicker();
      }
    };

    const onDocumentKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePicker();
      }
    };

    const onWindowResize = () => {
      updatePickerPosition();
    };

    const onWindowScroll = () => {
      updatePickerPosition();
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);
    window.addEventListener('resize', onWindowResize);
    window.addEventListener('scroll', onWindowScroll, true);

    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('scroll', onWindowScroll, true);
    };
  }, [closePicker, isOpen, updatePickerPosition]);

  useEffect(() => {
    if (!scrollRef.current || normalizedSearch) {
      return;
    }

    const onScroll = () => {
      const currentSectionId = getCurrentSectionId(
        tabSections,
        sectionRefs,
        scrollRef.current.getBoundingClientRect()
      );

      if (currentSectionId) {
        setActiveTab(currentSectionId);
      }
    };

    const container = scrollRef.current;
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [normalizedSearch, tabSections]);

  const onToneChange = (tone) => {
    setSelectedTone(tone);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(`${recentStorageKey}_tone`, String(tone));
      } catch (error) {
        // localStorage can fail in private mode or quota limits.
      }
    }
  };

  const getEmojiLabel = (emoji) => searchIndex[emoji]?.label || 'Emoji';

  const onTabClick = (sectionId) => {
    setActiveTab(sectionId);
    setIsOverflowOpen(false);
    const node = sectionRefs.current[sectionId];
    if (node && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: getScrollTargetTop(scrollRef.current, node),
        behavior: 'auto',
      });
    }
  };

  const onMoreTabsClick = () => {
    setIsOverflowOpen((prev) => !prev);
  };

  const isOverflowActive = overflowTabs.some((section) => section.id === activeTab);

  return (
    <Wrapper className='emojiPickerWrapper' ref={wrapperRef}>
      <EmojiButton
        type='button'
        className='emojiPickerToggle'
        onClick={onToggleOpen}
        aria-label={ariaLabel}
      >
        {buttonEmoji}
      </EmojiButton>
      {isOpen &&
        createPortal(
          <EmojiPicker className='emojiPicker' ref={pickerRef} style={pickerStyle}>
            <EmojiControlsRow>
              <EmojiTabs>
                {fixedTabs.map((section) => (
                  <EmojiTab
                    key={section.id}
                    type='button'
                    isActive={activeTab === section.id}
                    onClick={() => onTabClick(section.id)}
                    title={section.title}
                  >
                    <EmojiTabIcon>
                      {CATEGORY_TAB_META[section.id]?.icon || CATEGORY_FALLBACK_ICON}
                    </EmojiTabIcon>
                  </EmojiTab>
                ))}
                {showMoreTabs && (
                  <div style={{ position: 'relative' }}>
                    <EmojiTab
                      ref={overflowButtonRef}
                      type='button'
                      isActive={isOverflowOpen || isOverflowActive}
                      onClick={onMoreTabsClick}
                      title='More categories'
                    >
                      <EmojiTabIcon>{'\u22EF'}</EmojiTabIcon>
                    </EmojiTab>
                    {isOverflowOpen && (
                      <EmojiOverflowMenu ref={overflowMenuRef}>
                        {overflowTabs.map((section) => (
                          <EmojiOverflowMenuItem
                            key={section.id}
                            type='button'
                            isActive={activeTab === section.id}
                            onClick={() => onTabClick(section.id)}
                          >
                            <span>{CATEGORY_TAB_META[section.id]?.icon || CATEGORY_FALLBACK_ICON}</span>
                            <span>{section.title}</span>
                          </EmojiOverflowMenuItem>
                        ))}
                      </EmojiOverflowMenu>
                    )}
                  </div>
                )}
              </EmojiTabs>
            </EmojiControlsRow>
            <EmojiSearchInput
              ref={searchInputRef}
              className='emojiSearchInput'
              placeholder='Search emoji'
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <EmojiPickerScroll ref={scrollRef}>
              {!normalizedSearch && recentEmojis.length > 0 && (
                <div
                  ref={(node) => {
                    sectionRefs.current.recent = node;
                  }}
                >
                  <EmojiSectionTitle>Recently used</EmojiSectionTitle>
                  <EmojiGrid>
                    {recentEmojis.map((emoji) => (
                      <EmojiItem
                        key={`recent_${emoji}`}
                        type='button'
                        className='emojiPickerItem emojiPickerItemRecent'
                        title={getEmojiLabel(emoji)}
                        aria-label={getEmojiLabel(emoji)}
                        onClick={() => onEmojiClick(emoji)}
                      >
                        {resolveEmojiByTone(emoji)}
                      </EmojiItem>
                    ))}
                  </EmojiGrid>
                </div>
              )}

              {normalizedSearch ? (
                <div>
                  <EmojiSectionTitle>
                    {searchResults.length > 0
                      ? `Search results (${searchResults.length})`
                      : 'No emoji found'}
                  </EmojiSectionTitle>
                  {searchResults.length > 0 && (
                    <EmojiGrid>
                      {searchResults.map((emoji) => (
                        <EmojiItem
                          key={emoji}
                          type='button'
                          className='emojiPickerItem'
                          title={getEmojiLabel(emoji)}
                          aria-label={getEmojiLabel(emoji)}
                          onClick={() => onEmojiClick(emoji)}
                        >
                          {resolveEmojiByTone(emoji)}
                        </EmojiItem>
                      ))}
                    </EmojiGrid>
                  )}
                </div>
              ) : (
                emojiSections.map((section) => (
                  <div
                    key={section.id}
                    ref={(node) => {
                      sectionRefs.current[section.id] = node;
                    }}
                  >
                    <EmojiSectionTitle>{section.title}</EmojiSectionTitle>
                    <EmojiGrid>
                      {section.emojis.map((emoji) => (
                        <EmojiItem
                          key={emoji}
                          type='button'
                          className='emojiPickerItem'
                          title={getEmojiLabel(emoji)}
                          aria-label={getEmojiLabel(emoji)}
                          onClick={() => onEmojiClick(emoji)}
                        >
                          {resolveEmojiByTone(emoji)}
                        </EmojiItem>
                      ))}
                    </EmojiGrid>
                  </div>
                ))
              )}
            </EmojiPickerScroll>
            <ToneSelector>
              {TONE_BUTTONS.map((toneEmoji, tone) => (
                <ToneButton
                  key={tone}
                  type='button'
                  isActive={selectedTone === tone}
                  onClick={() => onToneChange(tone)}
                >
                  {toneEmoji}
                </ToneButton>
              ))}
            </ToneSelector>
          </EmojiPicker>,
          document.body
        )}
    </Wrapper>
  );
};

EmojiPickerButton.propTypes = {
  emojis: PropTypes.arrayOf(PropTypes.string),
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    emojis: PropTypes.arrayOf(PropTypes.string),
  })),
  searchIndex: PropTypes.objectOf(PropTypes.shape({
    label: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    order: PropTypes.number,
    group: PropTypes.number,
    skins: PropTypes.objectOf(PropTypes.string),
  })),
  onSelect: PropTypes.func,
  ariaLabel: PropTypes.string,
  buttonEmoji: PropTypes.string,
  recentStorageKey: PropTypes.string,
  maxRecent: PropTypes.number,
};

EmojiPickerButton.defaultProps = {
  emojis: [],
  sections: popularEmojiSections,
  searchIndex: popularEmojiSearch,
  onSelect: () => {},
  ariaLabel: 'Add emoji',
  buttonEmoji: '\u{1F60A}',
  recentStorageKey: 'novotalk_recent_emojis',
  maxRecent: 18,
};

export default EmojiPickerButton;
